/* ==========================================================================
   Coffee quiz — "Find your Tiny Tiger"

   Drives sections/coffee-quiz.liquid. Everything it needs is already in the
   DOM: the questions, the options and their traits, and a card for every
   coffee. This file never fetches a product and never builds markup — it moves
   the `hidden` attribute around and scores what Liquid already rendered.

   Scoring is deliberately the dumbest thing that works: each option awards a
   list of traits, each coffee claims a list of traits, and a coffee's score is
   how many of the awarded traits it claims — counted with repeats, so a trait
   asked for twice across the quiz weighs twice. Ties fall to editor order,
   which gives the merchant a knob without inventing a settings field for it.
   ========================================================================== */

const SELECT_BEAT = 180; // let a tapped answer show as selected before moving on
const LOADING_BEAT = 750; // long enough to feel considered, short enough to forgive

/** Traits and grinds are typed by hand in the theme editor. Normalise hard. */
function splitTraits(value) {
  return (value || '')
    .toLowerCase()
    .split(/[,\s]+/)
    .map((trait) => trait.trim())
    .filter(Boolean);
}

function normalizeGrind(value) {
  return (value || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim();
}

/* A brew answer may name more than one acceptable grind, best first: a South
   Indian filter wants the "South Indian Filter" grind, but only Velvet Mocha is
   sold that way, and "Fine" is the right answer for every other bag. Split on
   commas only -- grind names have spaces in them ("Whole Beans"). */
function grindPreferences(value) {
  return (value || '')
    .split(',')
    .map(normalizeGrind)
    .filter(Boolean);
}

/** "Moka pot, AeroPress or ..." reads mid-sentence as "moka pot, ...". Only
    the first letter moves, so "AeroPress" and "V60" keep their capitals, and
    a first word that is a name ("French press", "South Indian filter") is
    left alone entirely. */
const PROPER_FIRST_WORDS = ['french', 'south', 'aeropress', 'v60', 'chemex', 'turkish'];

function lowerFirst(value) {
  const text = (value || '').trim();
  const first = text.split(/[\s,]+/)[0].toLowerCase();
  if (PROPER_FIRST_WORDS.includes(first)) return text;
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/* The shop's money format is "Rs. {{amount}}" -- comma thousands, two
   decimals, e.g. Rs. 2,300.00 -- which is what every price on the page
   already reads. Prices arrive in paise. */
function formatMoney(paise) {
  const amount = (paise / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return 'Rs. ' + amount;
}

class CoffeeQuiz {
  constructor(root) {
    this.root = root;
    this.intro = root.querySelector('[data-quiz-intro]');
    this.questions = Array.from(root.querySelectorAll('[data-quiz-question]'));
    this.loading = root.querySelector('[data-quiz-loading]');
    this.results = root.querySelector('[data-quiz-results]');
    this.progress = root.querySelector('[data-quiz-progress]');
    this.rail = root.querySelector('[data-quiz-rail]');
    this.count = root.querySelector('[data-quiz-count]');
    this.back = root.querySelector('[data-quiz-back]');
    this.live = root.querySelector('[data-quiz-live]');
    this.outcomes = Array.from(root.querySelectorAll('[data-quiz-outcome]'));
    this.noscript = root.querySelector('[data-quiz-noscript]');

    // answers[i] is the chosen option index for question i, or undefined.
    this.answers = [];
    this.timer = null;

    if (!this.questions.length || !this.outcomes.length) return;

    if (this.noscript) this.noscript.remove();

    this.bindOptions();
    this.bindControls();
    this.bindEmail();
    this.bindBuy();

    // A results hash makes a finished quiz shareable and survivable across a
    // refresh -- worth having, because the email we send links back here.
    if (!this.restoreFromHash()) this.goToIntro();
  }

  bindOptions() {
    this.questions.forEach((question, questionIndex) => {
      const options = Array.from(question.querySelectorAll('[data-quiz-option]'));
      options.forEach((option, optionIndex) => {
        option.addEventListener('click', () => {
          options.forEach((other) => other.setAttribute('aria-pressed', 'false'));
          option.setAttribute('aria-pressed', 'true');
          this.answers[questionIndex] = optionIndex;

          window.clearTimeout(this.timer);
          this.timer = window.setTimeout(() => {
            if (questionIndex + 1 < this.questions.length) {
              this.goToQuestion(questionIndex + 1);
            } else {
              this.finish();
            }
          }, SELECT_BEAT);
        });
      });
    });
  }

  bindControls() {
    if (this.back) {
      this.back.addEventListener('click', () => {
        const current = this.questions.findIndex((panel) => !panel.hidden);
        if (current > 0) {
          this.goToQuestion(current - 1);
        } else {
          this.goToIntro();
        }
      });
    }

    const start = this.root.querySelector('[data-quiz-start]');
    if (start) start.addEventListener('click', () => this.goToQuestion(0));

    const restart = this.root.querySelector('[data-quiz-restart]');
    if (restart) {
      restart.addEventListener('click', () => {
        this.answers = [];
        this.root.querySelectorAll('[data-quiz-option]').forEach((option) => {
          option.setAttribute('aria-pressed', 'false');
        });
        this.writeHash(null);
        this.goToIntro();
      });
    }
  }

  /* ---------------------------------------------------------------- panels */

  hideAll() {
    window.clearTimeout(this.timer);
    if (this.intro) this.intro.hidden = true;
    this.questions.forEach((panel) => {
      panel.hidden = true;
    });
    if (this.loading) this.loading.hidden = true;
    if (this.results) this.results.hidden = true;
  }

  goToIntro() {
    this.hideAll();
    if (this.intro) this.intro.hidden = false;
    if (this.progress) this.progress.hidden = true;
  }

  goToQuestion(index) {
    this.hideAll();
    const panel = this.questions[index];
    if (!panel) return;

    panel.hidden = false;
    if (this.progress) this.progress.hidden = false;
    if (this.back) this.back.hidden = false;

    const step = index + 1;
    const total = this.questions.length;
    if (this.count) this.count.textContent = step + ' of ' + total;
    // Counts the question on screen, so question 1 already shows a sliver.
    if (this.rail) this.rail.style.width = (step / total) * 100 + '%';
    if (this.live) this.live.textContent = 'Question ' + step + ' of ' + total;

    const heading = panel.querySelector('[data-quiz-question-heading]');
    if (heading) heading.focus({ preventScroll: true });
    this.scrollIntoView();
  }

  finish() {
    this.hideAll();
    if (this.progress) this.progress.hidden = false;
    if (this.back) this.back.hidden = true;
    if (this.rail) this.rail.style.width = '100%';
    if (this.count) this.count.textContent = 'Nearly there';
    if (this.loading) this.loading.hidden = false;
    this.scrollIntoView();

    /* The loading beat is not decoration — it is the window in which the
       winner's photographs load. The outcome images stay lazy because there
       are eighteen of them and at most three are ever seen, so nothing has
       fetched them at this point. Flip just the three about to be shown to
       eager and the beat covers the fetch, instead of the visitor watching an
       empty card fill in underneath the answer. */
    this.warmImages(this.rank().slice(0, 3));

    this.timer = window.setTimeout(() => {
      this.writeHash(this.answers);
      this.showResults();
    }, LOADING_BEAT);
  }

  /** Start fetching the images for a set of ranked outcomes, hero and card. */
  warmImages(ranked) {
    const handles = ranked.map(({ outcome }) => outcome.dataset.handle);
    const eager = (scope) => {
      scope.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    };

    ranked.forEach(({ outcome }) => eager(outcome));

    // The runner-up cards are a separate list, not children of the outcomes.
    this.root.querySelectorAll('[data-quiz-alt]').forEach((card) => {
      if (handles.includes(card.dataset.handle)) eager(card);
    });
  }

  scrollIntoView() {
    const top = this.root.getBoundingClientRect().top + window.scrollY;
    // Only pull the page up; scrolling down to the quiz mid-answer is jarring.
    if (window.scrollY > top) {
      window.scrollTo({ top: top - 24, behavior: 'smooth' });
    }
  }

  /* --------------------------------------------------------------- scoring */

  selected() {
    return this.questions
      .map((question, index) => {
        const optionIndex = this.answers[index];
        if (optionIndex == null) return null;
        return question.querySelectorAll('[data-quiz-option]')[optionIndex] || null;
      })
      .filter(Boolean);
  }

  rank() {
    const awarded = this.awardedTraits();

    const scored = this.outcomes
      .map((outcome, index) => {
        const claimed = splitTraits(outcome.dataset.traits);
        const score = claimed.reduce(
          (total, trait) => total + awarded.filter((given) => given === trait).length,
          0
        );
        return { outcome, score, index };
      })
      .sort((a, b) => b.score - a.score || a.index - b.index);

    /* Counting shared traits cannot express "never show this one". Decaf is the
       case that proves it: someone who wants decaf and also wants dark and
       milky will out-score the single decaf bag with a caffeinated one, and no
       amount of weighting fixes that reliably. So two traits are hard gates
       rather than points -- `requires` keeps a coffee out unless the trait was
       awarded, `excluded-by` drops it when the trait was awarded. */
    const qualifies = scored.filter(({ outcome }) => {
      const requires = splitTraits(outcome.dataset.requires);
      const excludedBy = splitTraits(outcome.dataset.excludedBy);
      if (requires.length && !requires.every((trait) => awarded.includes(trait))) return false;
      if (excludedBy.some((trait) => awarded.includes(trait))) return false;
      return true;
    });

    // A gate that rules out everything is a misconfiguration, not an answer.
    // Fall back to the ungated ranking so the quiz always has something to say.
    return qualifies.length ? qualifies : scored;
  }

  /** The brew question is whichever option carries a grind; usually question 1. */
  chosenGrind() {
    const option = this.selected().find((candidate) => candidate.dataset.grind);
    if (!option) return null;
    return {
      grind: option.dataset.grind,
      brew: option.dataset.brew || (option.textContent || '').trim(),
      traits: splitTraits(option.dataset.traits),
    };
  }

  /* Every trait the answers awarded, repeats and all. */
  awardedTraits() {
    const awarded = [];
    this.selected().forEach((option) => {
      awarded.push(...splitTraits(option.dataset.traits));
    });
    return awarded;
  }

  /* "How do you drink it" awards `milk` or `black` (and "depends on the day"
     neither), so the match's blurb follows the answer. The default blurb is
     kept on the element the first time, so going back and changing the answer
     can return to it. */
  applyBlurb(outcome) {
    const blurb = outcome.querySelector('[data-quiz-blurb]');
    if (!blurb) return;
    if (blurb.dataset.blurbDefault == null) blurb.dataset.blurbDefault = blurb.textContent;

    const awarded = this.awardedTraits();
    let text = blurb.dataset.blurbDefault;
    if (awarded.includes('milk') && blurb.dataset.blurbMilk) text = blurb.dataset.blurbMilk;
    else if (awarded.includes('black') && blurb.dataset.blurbBlack) text = blurb.dataset.blurbBlack;
    blurb.textContent = text;
  }

  showResults() {
    // Not optional: finish() leaves the loading panel up while it waits, and
    // this is what takes it back down. Without it the paws and "sniffing out
    // your coffee" sit above the answer forever.
    this.hideAll();

    const ranked = this.rank();
    const [best, ...rest] = ranked;
    if (!best) return;

    this.outcomes.forEach((outcome) => {
      outcome.hidden = true;
    });

    best.outcome.hidden = false;
    this.applyBlurb(best.outcome);
    this.applyGrind(best.outcome);

    const alts = this.results ? this.results.querySelector('[data-quiz-alts]') : null;
    if (alts) {
      const cards = Array.from(alts.querySelectorAll('[data-quiz-alt]'));
      cards.forEach((card) => {
        card.hidden = true;
      });

      const runnersUp = rest
        .slice(0, 2)
        .map(({ outcome }) => cards.find((card) => card.dataset.handle === outcome.dataset.handle))
        .filter(Boolean);

      runnersUp.forEach((card) => {
        card.hidden = false;
      });
      alts.hidden = runnersUp.length === 0;
    }

    if (this.results) this.results.hidden = false;
    if (this.progress) this.progress.hidden = true;
    if (this.live) this.live.textContent = 'Your match is ready.';

    const heading = this.results ? this.results.querySelector('[data-quiz-results-heading]') : null;
    if (heading) heading.focus({ preventScroll: true });
    this.scrollIntoView();
  }

  /* The match's Size and Grind pickers. The brew answer pre-selects a grind
     when this coffee is sold in one it asks for; size starts on the first (the
     single bag). Either can then be changed, and every change re-resolves the
     variant: its id goes into the form, its price into the button, and the
     product link follows it. */
  applyGrind(outcome) {
    const line = outcome.querySelector('[data-quiz-grind]');
    const choice = this.chosenGrind();
    const variants = this.variantsOf(outcome);

    let recommended = null;
    if (choice && choice.grind) {
      grindPreferences(choice.grind).some((wanted) => {
        const matches = variants.filter((candidate) => normalizeGrind(candidate.grindLabel) === wanted);
        recommended = matches.find((candidate) => candidate.available) || matches[0] || null;
        return Boolean(recommended);
      });
    }

    if (recommended) {
      const radio = Array.from(outcome.querySelectorAll('[data-quiz-grind-option]')).find(
        (input) => input.value === recommended.grind
      );
      if (radio) radio.checked = true;
    }

    if (line) {
      const text = line.querySelector('[data-quiz-grind-text]');
      if (recommended && text) {
        const template = line.dataset.template || "We've picked [grind] for your [brew].";
        let sentence = template
          .replace('[grind]', recommended.grindLabel)
          .replace('[brew]', lowerFirst(choice.brew));
        // Powder is a medium grind, coarser than a South Indian filter wants
        // (blog posts 01 and 05 say the same). It stays the pick, since most
        // buyers have no grinder, but the result says so honestly.
        if (
          line.dataset.filterNote &&
          normalizeGrind(recommended.grindLabel) === 'powder' &&
          choice.traits.includes('filter')
        ) {
          sentence += ' ' + line.dataset.filterNote;
        }
        text.textContent = sentence;
        line.hidden = false;
      } else {
        // Not sold in that grind (Velvet Mocha is filter-only), or no brew
        // answer: say nothing rather than promise a grind we cannot ship.
        line.hidden = true;
      }
    }

    this.syncVariant(outcome);
  }

  variantsOf(outcome) {
    if (!outcome._variants) {
      try {
        outcome._variants = JSON.parse(outcome.dataset.variants || '[]');
      } catch (error) {
        outcome._variants = [];
      }
    }
    return outcome._variants;
  }

  /** The variant the pickers currently describe, written into the form. */
  syncVariant(outcome) {
    const variants = this.variantsOf(outcome);
    const size = outcome.querySelector('[data-quiz-size]:checked');
    const grind = outcome.querySelector('[data-quiz-grind-option]:checked');

    const variant =
      variants.find(
        (candidate) =>
          (!size || candidate.size === size.value) && (!grind || candidate.grind === grind.value)
      ) || null;

    const input = outcome.querySelector('[data-quiz-variant]');
    const button = outcome.querySelector('[data-quiz-add]');
    const label = outcome.querySelector('[data-quiz-add-label]');
    const price = outcome.querySelector('[data-quiz-price]');
    const cta = outcome.querySelector('[data-quiz-cta]');
    const qty = outcome.querySelector('[data-quiz-qty-input]');

    if (!variant) return;

    if (input) input.value = variant.id;
    if (cta && cta.dataset.url) cta.href = cta.dataset.url + '?variant=' + variant.id;

    if (label) {
      if (!label.dataset.original) label.dataset.original = label.textContent;
      label.textContent = variant.available
        ? label.dataset.original
        : this.root.dataset.soldOutLabel || 'Sold out';
    }
    if (button && !button.dataset.busy) button.disabled = !variant.available;

    if (price) {
      const quantity = Math.max(1, parseInt(qty ? qty.value : '1', 10) || 1);
      price.textContent = formatMoney(variant.price * quantity);
      price.hidden = !variant.available;
    }
  }

  /* ------------------------------------------------------------------ hash */

  writeHash(answers) {
    const url = new URL(window.location.href);
    if (answers && answers.length) {
      url.hash = 'match=' + answers.join('-');
    } else {
      url.hash = '';
    }
    window.history.replaceState(null, '', url.toString());
  }

  restoreFromHash() {
    const match = /match=([\d-]+)/.exec(window.location.hash);
    if (!match) return false;

    const answers = match[1].split('-').map((value) => parseInt(value, 10));
    if (answers.length !== this.questions.length || answers.some(Number.isNaN)) return false;

    // Reject a hand-edited hash that points past the end of a question.
    const valid = answers.every((optionIndex, questionIndex) => {
      const options = this.questions[questionIndex].querySelectorAll('[data-quiz-option]');
      return optionIndex >= 0 && optionIndex < options.length;
    });
    if (!valid) return false;

    this.answers = answers;
    answers.forEach((optionIndex, questionIndex) => {
      const options = this.questions[questionIndex].querySelectorAll('[data-quiz-option]');
      options.forEach((option, index) => {
        option.setAttribute('aria-pressed', index === optionIndex ? 'true' : 'false');
      });
    });

    this.showResults();
    return true;
  }

  /* ------------------------------------------------------------------- buy */

  bindBuy() {
    this.root.querySelectorAll('[data-quiz-buy]').forEach((form) => {
      const input = form.querySelector('[data-quiz-qty-input]');
      const outcome = form.closest('[data-quiz-outcome]');
      const refresh = () => {
        if (outcome) this.syncVariant(outcome);
      };

      form.querySelectorAll('[data-quiz-size], [data-quiz-grind-option]').forEach((radio) => {
        radio.addEventListener('change', refresh);
      });

      form.querySelectorAll('[data-quiz-qty]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = parseInt(btn.dataset.quizQty, 10);
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
          input.value = String(next);
          form.querySelector('[data-quiz-qty="-1"]').disabled = next <= 1;
          refresh();
        });
      });

      input.addEventListener('change', () => {
        const n = Math.max(1, parseInt(input.value, 10) || 1);
        input.value = String(n);
        form.querySelector('[data-quiz-qty="-1"]').disabled = n <= 1;
        refresh();
      });

      form.querySelector('[data-quiz-qty="-1"]').disabled = true;
      form.addEventListener('submit', (event) => this.addToCart(event, form));
    });
  }

  async addToCart(event, form) {
    event.preventDefault();

    const button = form.querySelector('[data-quiz-add]');
    const label = form.querySelector('[data-quiz-add-label]');
    const outcome = form.closest('[data-quiz-outcome]');
    const status = outcome ? outcome.querySelector('[data-quiz-added]') : null;
    const qtyInput = form.querySelector('[data-quiz-qty-input]');
    const quantity = Math.max(1, parseInt(qtyInput ? qtyInput.value : '1', 10) || 1);

    if (button) {
      button.disabled = true;
      button.dataset.busy = 'true';
    }

    /* Open the cart drawer on the tap, the way the product page does (see
       #openCartDrawerEarly in assets/product-form.js): Shopify takes most of
       a second to confirm an add, and the drawer shows a placeholder row
       until it has. */
    const drawer = document.querySelector('cart-drawer-component[auto-open]');
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.setAttribute('data-adding', '');
      drawer.showDialog();
    }

    const body = new FormData(form);

    /* Ask the cart to re-render whichever sections are showing cart items, the
       way the theme's own product form does. Without this the drawer shows the
       cart as it was before this add. */
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => node.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    try {
      const response = await fetch(this.root.dataset.cartAddUrl || '/cart/add', {
        method: 'POST',
        body,
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      });
      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Add failed');

      /* `source: 'product-form-component'` tells the cart icon to add this
         quantity to its count rather than replace the count with it -- which
         saves waiting on a second request to /cart.js just to learn the total,
         the thing that used to hold this confirmation back by ~250ms. */
      form.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'coffee-quiz',
            data: { source: 'product-form-component', itemCount: quantity, sections: payload.sections },
          },
        })
      );

      if (status) {
        status.textContent = this.root.dataset.addedLabel || 'Added to your cart.';
        status.classList.remove('tt-quiz__added--error');
        status.hidden = false;
      }
      if (label) label.textContent = this.root.dataset.addedLabel || 'Added';
      window.setTimeout(() => {
        if (button) delete button.dataset.busy;
        if (outcome) this.syncVariant(outcome);
      }, 2000);
    } catch (error) {
      if (drawer && typeof drawer.closeDialog === 'function') drawer.closeDialog();
      if (status) {
        status.textContent = this.root.dataset.addErrorLabel || "That didn't go through. Try again.";
        status.classList.add('tt-quiz__added--error');
        status.hidden = false;
      }
      if (button) delete button.dataset.busy;
      if (outcome) this.syncVariant(outcome);
    } finally {
      if (drawer) drawer.removeAttribute('data-adding');
    }
  }

  /* ----------------------------------------------------------------- email */

  bindEmail() {
    const form = this.root.querySelector('[data-quiz-email-form]');
    if (!form) return;

    const status = this.root.querySelector('[data-quiz-email-status]');
    const input = form.querySelector('input[type="email"]');

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!input || !input.value) return;

      const button = form.querySelector('button');
      if (button) button.disabled = true;

      const body = new FormData();
      body.append('form_type', 'customer');
      body.append('utf8', '✓');
      body.append('contact[email]', input.value);
      body.append('contact[tags]', 'coffee-quiz');

      try {
        const response = await fetch(form.dataset.action, {
          method: 'POST',
          body,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error('Subscribe failed');
        if (status) {
          status.textContent = form.dataset.success || 'You are on the list.';
          status.hidden = false;
        }
        form.hidden = true;
      } catch (error) {
        if (status) {
          status.textContent = form.dataset.error || 'That did not go through. Try again in a moment.';
          status.hidden = false;
        }
        if (button) button.disabled = false;
      }
    });
  }
}

document.querySelectorAll('[data-tt-quiz]').forEach((root) => new CoffeeQuiz(root));
